import React, { useMemo, useState } from 'react';

import { Alert, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';

import { useHomepageGeoValues, useHomepageMetrics } from '~/hooks/apis/experiences';

import { GeoFilter } from '~/services/ExperiencesService';
import { Dayjs, SYDNEY_TIMEZONE, getDayJs } from '~/services/TimeService';

import {
  getHomepageDateParams,
  getHomepageDefaultDateRange,
  sortHomepageRows,
  toHomepageDisplayRow,
} from '~/utils/experienceUtils';

import HomepageMetricsFilters, { CountryOption } from './HomepageMetricsFilters';
import HomepageMetricsTable from './HomepageMetricsTable';
import { HomepageMetricsSortDirection, HomepageMetricsSortField } from './types';

const VIEWS_DESCRIPTION =
  'Distinct sessions with an impression per section and Sydney day, summed over the selected period.';

export default function HomepageMetrics() {
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(getHomepageDefaultDateRange);
  const [appliedDateRange, setAppliedDateRange] = useState(dateRange);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [geoFilter, setGeoFilter] = useState<GeoFilter>({});
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<HomepageMetricsSortField>('cpOrder');
  const [sortDirection, setSortDirection] = useState<HomepageMetricsSortDirection>('asc');
  const dates = getHomepageDateParams(appliedDateRange);
  const isEditingDateRange = !getHomepageDateParams(dateRange);
  const hasValidDateRange = !!dates;
  const params = { ...dates, ...geoFilter };
  const metrics = useHomepageMetrics(params, !!dates);
  const regions = useHomepageGeoValues(
    { ...dates, country: selectedCountry?.iso2 ?? '', field: 'region' },
    !!dates && !!selectedCountry,
  );
  const cities = useHomepageGeoValues(
    { ...dates, country: selectedCountry?.iso2 ?? '', region: geoFilter.region, field: 'city' },
    !!dates && !!selectedCountry && !!geoFilter.region,
  );
  const rows = useMemo(() => {
    if (!hasValidDateRange) {
      return [];
    }
    const normalizedSearch = search.trim().toLowerCase();
    const filtered = (metrics.data?.result ?? [])
      .map(toHomepageDisplayRow)
      .filter(
        (row) =>
          !normalizedSearch ||
          row.displayName.toLowerCase().includes(normalizedSearch) ||
          row.sectionId.toLowerCase().includes(normalizedSearch),
      );
    return sortHomepageRows(filtered, sortField, sortDirection);
  }, [metrics.data, search, sortField, sortDirection, hasValidDateRange]);

  const handleSort = (field: HomepageMetricsSortField) => {
    if (field === sortField) {
      setSortDirection((value) => (value === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <Stack spacing={3}>
      <HomepageMetricsFilters
        dateRange={dateRange}
        selectedCountry={selectedCountry}
        geoFilter={geoFilter}
        regionOptions={regions.data ?? []}
        cityOptions={cities.data ?? []}
        regionLoading={regions.isFetching}
        cityLoading={cities.isFetching}
        maxDate={getDayJs().tz(SYDNEY_TIMEZONE).subtract(1, 'day')}
        onDateRangeChange={(range) => {
          const nextRange = !range[0] && !range[1] ? getHomepageDefaultDateRange() : range;
          setDateRange(nextRange);
          if (!getHomepageDateParams(nextRange)) {
            return;
          }
          setAppliedDateRange(nextRange);
          setGeoFilter({ country: selectedCountry?.iso2 });
        }}
        onCountryChange={(_, value) => {
          setSelectedCountry(value);
          setGeoFilter({ country: value?.iso2 });
        }}
        onRegionChange={(_, value) => {
          setGeoFilter((geo) => ({ country: geo.country, region: value ?? undefined }));
        }}
        onCityChange={(_, value) => {
          setGeoFilter((geo) => ({ ...geo, city: value ?? undefined }));
        }}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Search sections"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          sx={{ maxWidth: 400, flexGrow: 1 }}
        />
        {dates && (
          <Typography variant="body2" color="text.secondary">
            {dates.startDate} – {dates.endDate} (Sydney)
          </Typography>
        )}
        {metrics.data?.updatedAt && (
          <Typography variant="body2" color="text.secondary">
            Last source refresh: {getDayJs(metrics.data.updatedAt).tz(SYDNEY_TIMEZONE).format('D MMM YYYY HH:mm')}{' '}
            Sydney
          </Typography>
        )}
      </Stack>
      {isEditingDateRange && (
        <Alert severity="info">
          Complete a valid date range ending before today in Sydney. Results show the last applied period.
        </Alert>
      )}
      {(regions.isError || cities.isError) && <Alert severity="error">Failed to load geographic filter options.</Alert>}
      {dates && metrics.isPending && (
        <Box display="flex" justifyContent="center">
          <CircularProgress aria-label="Loading homepage metrics" />
        </Box>
      )}
      {dates && metrics.isError && <Alert severity="error">Failed to fetch current homepage metrics.</Alert>}
      {dates &&
        metrics.isSuccess &&
        (rows.length > 0 ? (
          <HomepageMetricsTable
            rows={rows}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            viewsDescription={VIEWS_DESCRIPTION}
          />
        ) : (
          <Alert severity="info">No homepage metrics match the selected period and filters.</Alert>
        ))}
    </Stack>
  );
}
